import { z } from "zod";

import { currentUiLocale } from "../i18n/index.js";
import { httpGet, httpPost } from "../lib/http";

export const ProjectSchema = z
  .object({
    /** A GitLab project id, or a GitHub repository stored as its negated
     *  id (`forgeOfProjectId` in `@loom/shared/forge`). */
    id: z.number().int(),
    name: z.string(),
    name_with_namespace: z.string(),
    path_with_namespace: z.string(),
    web_url: z.string(),
    last_activity_at: z.string().optional(),
    description: z.string().nullable().optional(),
    /**
     * Per-project Loom access level for the current user (access-control
     * feature). `"full"` → everything incl. Implement; `"create_only"` → create
     * + refine only; `null` → no access. Absent against a pre-feature server
     * (`.nullish()` keeps that parseable during a rolling deploy); consumers
     * treat absent the same as the server-level `accessEnforced: false` (all
     * access). Server enforces; this is UX-only.
     */
    loomLevel: z.enum(["create_only", "full"]).nullish(),
  })
  .passthrough();
export type Project = z.infer<typeof ProjectSchema>;

const ProjectsResponseSchema = z.object({
  projects: z.array(ProjectSchema),
  /**
   * Whether the server is enforcing per-project access. When `false` (or absent
   * against an older server, hence `.nullish()`), every project is treated as
   * full access regardless of `loomLevel`. Server-owned; UX reads it to decide
   * whether a null `loomLevel` means "no access" or "enforcement off".
   */
  accessEnforced: z.boolean().nullish(),
});

export interface ProjectsResult {
  readonly projects: Project[];
  readonly accessEnforced: boolean;
}

/**
 * Whether the current user has ANY Loom access to a project. With enforcement
 * off, every project is open. With it on, a null `loomLevel` means no access.
 */
export const hasProjectAccess = (
  project: Project,
  accessEnforced: boolean,
): boolean => !accessEnforced || (project.loomLevel ?? null) !== null;

/**
 * Whether the user may Implement on a project — `loomLevel === "full"` only.
 * With enforcement off, treat everything as full. Server enforces; this is the
 * non-authoritative UX gate for the Implement affordance.
 */
export const canImplement = (
  project: Project,
  accessEnforced: boolean,
): boolean => !accessEnforced || project.loomLevel === "full";

export interface FetchProjectsOptions {
  /** Server-side substring search over name + path + namespace. */
  readonly search?: string;
  /**
   * Ask for the most-recently-active projects first (a small, fast page)
   * instead of the full membership walk. The interactive picker sets this
   * for its cold/empty-query load; callers that need every project (e.g. the
   * id → project lookup) omit it.
   */
  readonly recent?: boolean;
  /**
   * Also list the user's GitHub repositories (negative ids), merged by last
   * activity. Only the Focus picker asks: the server leaves them out when
   * GitHub is off or not connected, and without the flag the answer is the
   * GitLab list alone.
   */
  readonly includeGithub?: boolean;
}

/** Full envelope — the picker needs `accessEnforced` to gate the cards. */
export const fetchProjectsResult = async (
  opts: FetchProjectsOptions = {},
): Promise<ProjectsResult> => {
  const params = new URLSearchParams();
  if (opts.search !== undefined && opts.search.trim() !== "") {
    params.set("q", opts.search.trim());
  }
  if (opts.recent) params.set("recent", "1");
  if (opts.includeGithub) params.set("github", "1");
  const qs = params.toString();
  const { projects, accessEnforced } = await httpGet(
    `/api/projects${qs ? `?${qs}` : ""}`,
    ProjectsResponseSchema,
  );
  return { projects, accessEnforced: accessEnforced ?? false };
};

/** Projects array only — the common case (Focus picker, AccessPage grant
 *  search, id lookup). Callers that need `accessEnforced` use the envelope. */
export const fetchProjects = async (
  opts: FetchProjectsOptions = {},
): Promise<Project[]> => (await fetchProjectsResult(opts)).projects;

const NewSessionResponseSchema = z.object({
  sessionId: z.string(),
  configSource: z.enum(["file", "default"]),
  warnings: z.array(z.string()),
});

export interface CreateSessionOptions {
  /** Focus "Implement an issue" opens an autonomous implement→MR run. */
  readonly intent?: "implement";
  /** Required when `intent === "implement"`. */
  readonly issueIid?: number;
  /** Optional display context forwarded to the seed prompt + pipeline UI. */
  readonly issueTitle?: string;
  readonly issueWebUrl?: string | null;
}

export const createSession = async (
  projectId: number,
  options: CreateSessionOptions = {},
): Promise<z.infer<typeof NewSessionResponseSchema>> =>
  httpPost(
    "/sessions",
    {
      projectId,
      // The language the operator is reading Loom in. On a project whose
      // `.loom.yml` sets no locale, the agent is told to write in it by default
      // (and to follow the user if they write in another). Read at call time,
      // so a language switch reaches the next session without a reload.
      uiLocale: currentUiLocale(),
      ...(options.intent ? { intent: options.intent } : {}),
      ...(options.issueIid !== undefined ? { issueIid: options.issueIid } : {}),
      ...(options.issueTitle !== undefined
        ? { issueTitle: options.issueTitle }
        : {}),
      ...(options.issueWebUrl
        ? { issueWebUrl: options.issueWebUrl }
        : {}),
    },
    NewSessionResponseSchema,
  );
